import { memo, useEffect, useState } from 'react';
import type { Comment } from '../api/client';
import { AttachmentItem } from './AttachmentItem';

interface Props {
  comment: Comment;
  isEditing: boolean;
  isDeleting: boolean;
  isMine: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (commentId: string, body: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  getDownloadUrl: (attachmentId: string) => Promise<string>;
}

export const CommentItem = memo(
  function CommentItem({ comment: c, isEditing, isDeleting, isMine, onStartEdit, onCancelEdit, onSave, onDelete, getDownloadUrl }: Props): JSX.Element {
    const [editDraft, setEditDraft] = useState('');
    const [editBusy, setEditBusy] = useState(false);

    // Sync draft with comment body when entering/leaving edit mode
    useEffect(() => {
      setEditDraft(isEditing ? c.body : '');
    }, [isEditing]);

    async function handleSave() {
      if (!editDraft.trim()) return;
      setEditBusy(true);
      try {
        await onSave(c.commentId, editDraft.trim());
      } catch {
        // error surfaced by parent
      } finally {
        setEditBusy(false);
      }
    }

    return (
      <li
        className={`rounded p-3 text-sm bg-gray-800/50 border ${
          c.authorRole === 'staff' || c.authorRole === 'admin'
            ? 'border-l-4 border-l-red-500 border-gray-700'
            : 'border-gray-700'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-200">{c.authorDisplayName ?? c.authorEmail}</span>
            {(c.authorRole === 'staff' || c.authorRole === 'admin') && (
              <span className="text-xs bg-red-900/50 text-red-300 px-1.5 py-0.5 rounded border border-red-800/50">Staff</span>
            )}
            <span className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleString()}</span>
            {c.editedAt && <span className="text-xs text-gray-600 italic">(edited)</span>}
          </div>
          {isMine && !isEditing && (
            <div className="flex gap-2 shrink-0">
              <button className="text-xs text-gray-400 hover:text-white hover:underline" disabled={isDeleting} onClick={onStartEdit}>Edit</button>
              <button
                className="text-xs text-red-400 hover:underline disabled:opacity-50"
                disabled={isDeleting}
                onClick={() => onDelete(c.commentId)}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-2 mt-1">
            <textarea className="input w-full min-h-[80px]" value={editDraft} onChange={(e) => setEditDraft(e.target.value)} maxLength={5000} disabled={editBusy} />
            <div className="flex gap-2">
              <button className="btn-primary text-xs py-1 px-2" disabled={editBusy || !editDraft.trim()} onClick={handleSave}>{editBusy ? 'Saving…' : 'Save'}</button>
              <button className="btn-secondary text-xs py-1 px-2" disabled={editBusy} onClick={onCancelEdit}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <p className="whitespace-pre-wrap text-gray-300">{c.body}</p>
            {(c.attachments ?? []).length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-700">
                <div className="flex flex-wrap gap-3">
                  {c.attachments.map((a) => (
                    <AttachmentItem
                      key={a.attachmentId}
                      attachment={a}
                      getDownloadUrl={getDownloadUrl}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </li>
    );
  },
  // Re-render only when data or edit/delete mode changes — callback identity excluded
  (prev, next) =>
    prev.comment === next.comment &&
    prev.isEditing === next.isEditing &&
    prev.isDeleting === next.isDeleting &&
    prev.isMine === next.isMine,
);
